//Make a call to the GitHub API to get Copilot Metrics, the API is /api/github/orgs/toussaintt/copilot/usage
//Add the header Accept: application/vnd.github+json to the request
//Add also the Authorization: Bearer <token> header where <token> is hardcoded for now
//Also add X-GitHub-Api-Version: 2022-11-28 header
//Return the response from the API

import axios from "axios";

import { Metrics } from "../model/Metrics";
import organizationMockedResponse from '../assets/organization_response_sample.json';
import enterpriseMockedResponse from '../assets/enterprise_response_sample.json';
import config from '../config';

const headers = {
  Accept: "application/vnd.github+json",
  "X-GitHub-Api-Version": "2022-11-28",
  ...(config.github.token ? { Authorization: `token ${config.github.token}` } : {})
};

export const getMetricsApi = async (): Promise<Metrics[]> => {
  let response;
  let metricsData;

  if (config.mockedData) {
    console.log("Using mock data. Check VUE_APP_MOCKED_DATA variable.");
    response = config.scope.type === "organization" ? organizationMockedResponse : enterpriseMockedResponse;
    metricsData = response.map((item: any) => new Metrics(item));
  } else {
    response = await axios.get(
      `${config.github.apiUrl}/copilot/metrics`,
      {
        headers
      }
    );

    metricsData = transformNewMetricsFormat(response.data);
  }
  return metricsData;
};

function transformNewMetricsFormat(data: any): Metrics[] {
  // Sort data by date to ensure correct chart ordering
  const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  return sortedData.map((item: any) => {
    const totalSuggestions = calculateTotalSuggestions(item);
    const totalAcceptances = calculateTotalAcceptances(item);
    const totalLinesSuggested = calculateTotalLinesSuggested(item);
    const totalLinesAccepted = calculateTotalLinesAccepted(item);

    const transformedData = {
      day: item.date,
      // Code completion metrics
      total_suggestions_count: totalSuggestions,
      total_acceptances_count: totalAcceptances,
      total_lines_suggested: totalLinesSuggested,
      total_lines_accepted: totalLinesAccepted,
      // User metrics
      total_active_users: item.total_active_users || 0,
      total_engaged_users: item.total_engaged_users || 0,
      // Chat metrics
      total_chat_acceptances: item.copilot_ide_chat?.editors?.[0]?.models?.[0]?.total_chat_copy_events || 0,
      total_chat_turns: item.copilot_ide_chat?.editors?.[0]?.models?.[0]?.total_chats || 0,
      total_active_chat_users: item.copilot_ide_chat?.total_engaged_users || 0,
      // Calculated rates
      acceptance_rate: totalSuggestions > 0 ? (totalAcceptances / totalSuggestions) * 100 : 0,
      lines_acceptance_rate: totalLinesSuggested > 0 ? (totalLinesAccepted / totalLinesSuggested) * 100 : 0,
      // Breakdown data for language/editor charts
      breakdown: transformBreakdownData(item),
      // Editor-specific data
      editors: transformEditorData(item),
      // Language-specific data
      languages: transformLanguageData(item)
    };
    return new Metrics(transformedData);
  });
}

function transformEditorData(item: any): any[] {
  const editors: any[] = [];
  item.copilot_ide_code_completions?.editors?.forEach((editor: any) => {
    const editorData = {
      name: editor.name,
      total_engaged_users: editor.total_engaged_users || 0,
      total_suggestions: 0,
      total_acceptances: 0,
      total_lines_suggested: 0,
      total_lines_accepted: 0
    };

    editor.models?.forEach((model: any) => {
      model.languages?.forEach((lang: any) => {
        editorData.total_suggestions += lang.total_code_suggestions || 0;
        editorData.total_acceptances += lang.total_code_acceptances || 0;
        editorData.total_lines_suggested += lang.total_code_lines_suggested || 0;
        editorData.total_lines_accepted += lang.total_code_lines_accepted || 0;
      });
    });

    editors.push(editorData);
  });
  return editors;
}

function transformLanguageData(item: any): any[] {
  const languageMap = new Map();

  item.copilot_ide_code_completions?.editors?.forEach((editor: any) => {
    editor.models?.forEach((model: any) => {
      model.languages?.forEach((lang: any) => {
        if (!languageMap.has(lang.name)) {
          languageMap.set(lang.name, {
            name: lang.name,
            total_engaged_users: 0,
            total_suggestions: 0,
            total_acceptances: 0,
            total_lines_suggested: 0,
            total_lines_accepted: 0
          });
        }
        
        const langData = languageMap.get(lang.name);
        langData.total_engaged_users += lang.total_engaged_users || 0;
        langData.total_suggestions += lang.total_code_suggestions || 0;
        langData.total_acceptances += lang.total_code_acceptances || 0;
        langData.total_lines_suggested += lang.total_code_lines_suggested || 0;
        langData.total_lines_accepted += lang.total_code_lines_accepted || 0;
      });
    });
  });

  return Array.from(languageMap.values());
}

function calculateTotalSuggestions(item: any): number {
  return item.copilot_ide_code_completions?.editors?.reduce((total: number, editor: any) => {
    return total + editor.models?.reduce((modelTotal: number, model: any) => {
      return modelTotal + model.languages?.reduce((langTotal: number, lang: any) => {
        return langTotal + (lang.total_code_suggestions || 0);
      }, 0) || 0;
    }, 0) || 0;
  }, 0) || 0;
}

function calculateTotalAcceptances(item: any): number {
  return item.copilot_ide_code_completions?.editors?.reduce((total: number, editor: any) => {
    return total + editor.models?.reduce((modelTotal: number, model: any) => {
      return modelTotal + model.languages?.reduce((langTotal: number, lang: any) => {
        return langTotal + (lang.total_code_acceptances || 0);
      }, 0) || 0;
    }, 0) || 0;
  }, 0) || 0;
}

function calculateTotalLinesSuggested(item: any): number {
  return item.copilot_ide_code_completions?.editors?.reduce((total: number, editor: any) => {
    return total + editor.models?.reduce((modelTotal: number, model: any) => {
      return modelTotal + model.languages?.reduce((langTotal: number, lang: any) => {
        return langTotal + (lang.total_code_lines_suggested || 0);
      }, 0) || 0;
    }, 0) || 0;
  }, 0) || 0;
}

function calculateTotalLinesAccepted(item: any): number {
  return item.copilot_ide_code_completions?.editors?.reduce((total: number, editor: any) => {
    return total + editor.models?.reduce((modelTotal: number, model: any) => {
      return modelTotal + model.languages?.reduce((langTotal: number, lang: any) => {
        return langTotal + (lang.total_code_lines_accepted || 0);
      }, 0) || 0;
    }, 0) || 0;
  }, 0) || 0;
}

function transformBreakdownData(item: any): any[] {
  const breakdown: any[] = [];
  
  // Transform language breakdown
  item.copilot_ide_code_completions?.editors?.forEach((editor: any) => {
    editor.models?.forEach((model: any) => {
      model.languages?.forEach((lang: any) => {
        breakdown.push({
          language: lang.name,
          editor: editor.name,
          acceptances_count: lang.total_code_acceptances || 0,
          lines_suggested: lang.total_code_lines_suggested || 0,
          lines_accepted: lang.total_code_lines_accepted || 0,
          engaged_users: lang.total_engaged_users || 0
        });
      });
    });
  });

  return breakdown;
}

export const getTeams = async (): Promise<string[]> => {
  const response = await axios.get(`${config.github.apiUrl}/teams`, {
    headers
  });

  return response.data;
}

export const getTeamMetricsApi = async (): Promise<Metrics[]> => {
  console.log("Fetching metrics for team:", config.github.team);
  
  if (config.github.team && config.github.team.trim() !== '') {
    const response = await axios.get(
      `${config.github.apiUrl}/teams/${config.github.team}/copilot/metrics`,
      {
        headers
      }
    );

    return transformNewMetricsFormat(response.data);
  }
  
  return [];
};